import { Check } from 'lucide-react';
import { foundation } from '../../ui/tokens';
import { semantic as sem } from '../../ui/theme/semanticTokens';

const { color, spacePx, fontSize, fontWeight, fontFamily } = foundation;

/**
 * Distance from the wrapper's left edge to where the LABEL TEXT starts.
 * Use this in callers when they need to render auxiliary content
 * (description hints, lists, secondary lines) indented to align under
 * the label text rather than under the checkbox visual.
 *
 *   checkbox visual (18px) + gap (spacePx[2] = 8px) = 26px
 */
export const CHECKBOX_LABEL_OFFSET = 26;

/**
 * Labeled checkbox for form-style boolean choices (settings, modal toggles).
 *
 * Visual vocabulary matches `SelectableCheckbox` (18px rounded brand-fill
 * with a Lucide Check glyph) — keeps the design system consistent across
 * bulk-select rows and form surfaces.
 *
 * Implementation: hidden native `<input type="checkbox">` for full a11y
 * (keyboard, screen reader, form integration) wrapped in a `<label>` so
 * clicks anywhere on the label area toggle the input. The decorative
 * visual span is `aria-hidden` since the native input carries the state.
 *
 * @param {object}   props
 * @param {boolean}  props.checked
 * @param {(next: boolean) => void} props.onChange — receives the new boolean
 * @param {React.ReactNode} props.label
 * @param {React.ReactNode} [props.hint]   — optional muted line below the label
 * @param {boolean}  [props.disabled]
 * @param {string}   [props.testId]
 * @param {'start'|'center'} [props.align='start'] — 'center' for list rows (name + date): the box sits on the
 *   row's vertical centre like every other list row. 'start' (default) lines it up with the first line, for
 *   settings where a long hint wraps under the label.
 */
export default function LabeledCheckbox({
  checked,
  onChange,
  label,
  hint,
  disabled = false,
  testId,
  align = 'start',
}) {
  const centered = align === 'center';
  const handleChange = (e) => {
    if (disabled) return;
    onChange?.(e.target.checked);
  };

  return (
    <label
      style={{
        ...wrapperStyle,
        ...(centered ? { alignItems: 'center' } : null),
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {/* Hidden native input — drives state, accessibility, focus. The
          decorative visual span below mirrors its checked state. */}
      <input
        type="checkbox"
        checked={Boolean(checked)}
        onChange={handleChange}
        disabled={disabled}
        data-testid={testId}
        style={hiddenInputStyle}
      />
      <span
        aria-hidden
        style={{
          ...visualStyle,
          ...(centered ? { marginTop: 0 } : null),
          borderColor: checked ? color.brand.primary : color.neutral.border,
          backgroundColor: checked ? color.brand.primary : sem.bg.surface,
        }}
      >
        {checked && <Check size={12} strokeWidth={3} color={sem.fg.onPrimary} aria-hidden />}
      </span>
      <span style={textWrapStyle}>
        <span style={labelStyle}>{label}</span>
        {hint && <span style={hintStyle}>{hint}</span>}
      </span>
    </label>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────

const wrapperStyle = {
  display: 'inline-flex',
  alignItems: 'flex-start',
  gap: spacePx[2],
  position: 'relative',
  userSelect: 'none',
  WebkitTapHighlightColor: 'transparent',
  // Allow the wrapper to stack inside flex columns at full width.
  width: '100%',
};

// Native input covers the entire label row so every click (box, text, hint)
// lands directly on the input — fixes the dead-zone where clicking the
// visual checkbox span didn't toggle (the old 18×18 patch only reached the
// top-left corner). The decorative visual span and label text are siblings,
// so no interactive children conflict with the full-overlay hit target.
const hiddenInputStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  margin: 0,
  opacity: 0,
  cursor: 'inherit',
};

// Vertical alignment: the checkbox visual center should line up with the
// first-line text optical center. For 14px text at line-height 1.4 (line
// box = 19.6px), the text optical center sits ~9.8px from top of line. The
// checkbox center (9px) needs ~0.8px of top offset to match — we use 1px
// which reads visually crisp without sub-pixel rendering issues.
const visualStyle = {
  flexShrink: 0,
  width: 18,
  height: 18,
  marginTop: 1,
  borderRadius: 4,
  border: `1.5px solid ${color.neutral.border}`,
  backgroundColor: sem.bg.surface,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  transition: 'background-color 120ms ease, border-color 120ms ease',
};

const textWrapStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
};

const labelStyle = {
  fontSize: fontSize.sm,
  fontFamily: fontFamily.sans,
  color: color.neutral.text,
  lineHeight: 1.4,
  fontWeight: fontWeight.normal,
};

const hintStyle = {
  fontSize: fontSize.xs,
  color: color.neutral.textMuted,
  lineHeight: 1.4,
};
